import * as Sentry from "@sentry/vue";

export const CHUNK_LOAD_FAILED_MESSAGE = "We couldn't load that page. Check your internet connection and try again.";
const RETRY_NAVIGATION_MILLIS = 1500;

// Only reload to the same target once within this window, so a chunk that keeps
// failing (bad deploy, blocked script) can't put the browser in a reload loop.
const RELOAD_GUARD_KEY = 'fed-chunk-reload';
const RELOAD_GUARD_MILLIS = 30000;

/**
 * Locate the main app bundle script tag (e.g. https://host/js/app.abc123.js).
 * Its hash changes on every deploy, so a 404 for it means a new version has shipped.
 */
export function findAppSource(srcRoot) {
  const prefix = srcRoot || `${window.location.origin}/js/app.`;
  const scriptTags = document.getElementsByTagName('script');
  return [...scriptTags]
    .map(s => s.src)
    .find(src => src.startsWith(prefix));
}

/**
 * Resolves true when the currently loaded app bundle is no longer on the server,
 * i.e. a newer version has been deployed. Resolves false when it is still there,
 * and rejects on network failure (which tells us nothing about the version).
 */
export async function isAppSourceStale(appSource, method = 'GET') {
  const response = await fetch(appSource, {method});
  return response.status === 404;
}

export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = String(error.message || error);
  return /Loading (CSS )?chunk [^\s]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message);
}

function wasRecentlyReloadedTo(href) {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return false;
    const {href: lastHref, at} = JSON.parse(raw);
    return lastHref === href && (Date.now() - at) < RELOAD_GUARD_MILLIS;
  } catch (e) {
    return false;
  }
}

function rememberReloadTo(href) {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({href, at: Date.now()}));
  } catch (e) {
    // storage unavailable - reload anyway, the guard is best-effort
  }
}

// window.location cannot be stubbed under jsdom, so tests spy on these instead
export const browserNavigation = {
  assign: href => window.location.assign(href),
  reload: () => window.location.reload(),
};

/**
 * Full page load of the given resolved href (a fresh copy of the app on the
 * route the user was navigating to). Returns false if a reload to that href was
 * already attempted moments ago.
 */
export function reloadToHref(href) {
  if (wasRecentlyReloadedTo(href)) {
    return false;
  }
  rememberReloadTo(href);
  const target = new URL(href, window.location.href);
  const withoutHash = url => url.href.split('#')[0];
  const onlyHashDiffers = withoutHash(target) === withoutHash(new URL(window.location.href));
  if (onlyHashDiffers) {
    // the router runs in hash mode, so navigating to the target only changes
    // the fragment and the browser would not fetch a fresh copy of the app
    window.history.replaceState(null, '', target.href);
    browserNavigation.reload();
  } else {
    browserNavigation.assign(target.href);
  }
  return true;
}

// href of the navigation we already retried once, so a second failure reports
// to the user instead of looping
let retriedHref = null;

/**
 * Router error handler for lazy route chunks that fail to load.
 *
 * 1. If the deployed app bundle is gone, a new version shipped: reload the app
 *    at the route the user was navigating to.
 * 2. Otherwise (slow or dropped connection) retry the navigation once. Webpack
 *    forgets a failed chunk, so a second import() really re-requests it.
 * 3. If that fails too, tell the user rather than silently doing nothing.
 *
 * vue-router 3 only hands onError the error, so the caller records the pending
 * `to`/`from` routes (see router/index.js) and passes them in. `notify` shows
 * the failure message to the user.
 */
export async function handleChunkLoadError(router, error, to, from, {notify} = {}) {
  if (!isChunkLoadError(error)) {
    return false;
  }
  const targetHref = to ? router.resolve(to).href : window.location.href;
  const appSource = findAppSource();
  if (!appSource) {
    // dev server or unknown layout: nothing to compare, a plain reload is the best we can do
    return reloadToHref(targetHref);
  }
  let stale = false;
  try {
    stale = await isAppSourceStale(appSource);
  } catch (e) {
    // offline / network blip: not a version problem, fall through to the retry
  }
  if (stale) {
    return reloadToHref(targetHref);
  }

  if (retriedHref !== targetHref && to) {
    retriedHref = targetHref;
    await new Promise(resolve => setTimeout(resolve, RETRY_NAVIGATION_MILLIS));
    // only retry if the user is still where the failed navigation left them
    const stillWaiting = !from || router.currentRoute.fullPath === from.fullPath;
    if (stillWaiting) {
      router.push(to.fullPath).catch(() => {
        // a second failure comes back through onError and is reported below
      });
    }
    return true;
  }

  retriedHref = null;
  if (notify) {
    notify(CHUNK_LOAD_FAILED_MESSAGE);
  }
  Sentry.setContext('chunk-load-error', {appSource, targetHref});
  Sentry.captureException(error);
  return false;
}

export function checkVersion(options = {}) {
  const defaults = {
    checkInterval: 60000
  }
  options = {...defaults, ...options};
  const {
    srcRoot,
    checkInterval,
    debug = false,
    hostname,
    method = 'GET'
  } = options;

  if (!srcRoot) {
    console.warn('no src root');
    return;
  }

  if (hostname && window.location.hostname !== hostname) {
    console.log('check version not enabled for this host');
    return;
  }

  function checkExists(appSource, checkInterval) {
    const check = () => {
      isAppSourceStale(appSource, method)
        .then(stale => {
          if (stale) {
            alert('A new version of our website is available.');
            window.location.reload();
          } else {
            if (debug) {
              console.log('current version of website is still', appSource);
            }
            // queue next check
            checkExists(appSource, checkInterval);
          }
        })
        .catch(e => {
          Sentry.captureException(e);
        })
    };
    setTimeout(check, checkInterval);
  }

  try {
    const appSource = findAppSource(srcRoot);

    if (!appSource) {
      throw new Error('could not determine app source.  no version check available');
    } else {
      if (debug) {
        console.log('current version of website', appSource);
      }
    }
    checkExists(appSource, checkInterval);
  } catch (e) {
    console.error('check version setup failed', e);
    Sentry.setContext('error', {error: e});
    Sentry.captureException(new Error('failed to execute check version: ' + e.message));
  }
}
